import React, {
  useState, useEffect, useRef, useCallback, useMemo
} from 'react';
import classNames from 'classnames';
import { isAndroid } from 'react-device-detect';
import { usePhoneVerification } from '@app/hooks/usePhoneVerification';
import { useCountdownTimer } from '@app/hooks/useCountdownTimer';
import { formatPhoneNumber } from '@app/config/util';
import { addParamsToAnalitycs } from '@app/actions/application';
import { ANALYTICS_EVENTS } from '@common/analytics/AnalyticsEvents';
import classes from '../../registration/classes.module.scss';
import { SvgIcon } from '../../../../common/sprites';
import Spiner from '../../../../common/helpers/spiner';
import ResendOptionsModal from '../../registration/inner_components/resend_options_modal';

const { shared } = window;

interface PhoneVerificationBaseProps {
  phoneNumber: string;
  onVerify: (verificationCode: string) => Promise<void>;
  onResendClick?: () => void;
  onBack?: () => void;
  onEditNumber?: () => void;
  t: (key: string) => string;
  clearError?: () => void;
  loading?: boolean;
  error?: string | null;
  savedTimerEndsAt?: string | null;
  onTimerEndsAtChange?: (timerEndsAt: string | null) => void;
  titleKey?: string;
  subtitleKey?: string;
  showBackButton?: boolean;
  smsAttempts?: number;
  incrementSmsAttempts?: () => number;
  clickSource?: 'signup' | 'settings';
  /** Called when PUT /my/phone_number returns 422 with operation_status "sms_token"; parent should show popover and navigate */
  onSmsTokenError?: (message: string) => void;
}

/**
 * PhoneVerificationBase - Reusable component for phone verification
 * Handles code input, verification, resend logic, and error display
 * Accepts onVerify callback for different verification flows (registration, account settings)
 */
function PhoneVerificationBase({
  phoneNumber,
  onVerify,
  onResendClick: _onResendClick,
  onBack,
  onEditNumber,
  t,
  clearError,
  loading: parentLoading = false,
  error: parentError,
  savedTimerEndsAt,
  onTimerEndsAtChange,
  titleKey = 'smsVerification.verify_your_phone_number',
  subtitleKey,
  smsAttempts = 0,
  incrementSmsAttempts,
  clickSource = 'signup',
  onSmsTokenError
}: PhoneVerificationBaseProps) {
  const [code, setCode] = useState<string[]>(['', '', '', '']);
  const [isResendModalVisible, setIsResendModalVisible] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const singleInputRef = useRef<HTMLInputElement | null>(null);
  /** Ref to prevent duplicate verify requests (e.g. when effect re-runs or in Strict Mode) */
  const verificationInProgressRef = useRef(false);
  const {
    sendSmsCode, loading, error: phoneVerificationError, timerEndsAt
  } = usePhoneVerification({
    clickSource,
    smsAttempts,
    incrementSmsAttempts
  });

  // Use saved timerEndsAt from parent or current from hook
  const currentTimerEndsAt = timerEndsAt || savedTimerEndsAt;

  // Notify parent when timerEndsAt changes
  useEffect(() => {
    if (timerEndsAt && onTimerEndsAtChange) {
      onTimerEndsAtChange(timerEndsAt);
    }
  }, [timerEndsAt, onTimerEndsAtChange]);

  // Use countdown timer based on timer_ends_at from API
  const resendCooldown = useCountdownTimer(currentTimerEndsAt);

  // Focus helper: preventScroll avoids scroll-into-view jump; rAF minimizes gap so iOS may not hide keyboard
  const focusInput = useCallback((input: HTMLInputElement | null) => {
    if (!input) return;
    requestAnimationFrame(() => {
      input.focus({ preventScroll: true });
    });
  }, []);

  // Handle code verification
  const handleVerifyCode = useCallback(async (verificationCode: string) => {
    if (!verificationCode || verificationCode.length !== 4 || !verificationCode.match(/^\d{4}$/)) {
      return;
    }
    if (loading || parentLoading || isVerifying || verificationInProgressRef.current) {
      return;
    }

    verificationInProgressRef.current = true;
    setIsVerifying(true);
    setVerificationError(null);
    try {
      // Add clickSource to phone number verified event before API call
      window.store.dispatch(addParamsToAnalitycs({
        [ANALYTICS_EVENTS.PHONE_NUMBER_VERIFIED]: {
          'click source': clickSource
        }
      }));

      await onVerify(verificationCode);
    } catch (err: any) {
      // Set isVerifying to false immediately to hide spinner

      // Parse response body once if available (consumes body)
      let parsedBody: { operation_status?: string; operationStatus?: string; full_messages?: string[]; fullMessages?: string[]; error?: string } | null = null;
      if (err && typeof err.json === 'function') {
        try {
          parsedBody = await err.json();
        } catch (_e) {
          // ignore
        }
      }
      const body = parsedBody || err;

      // 422 with operation_status "sms_token" (e.g. from PUT /my/phone_number): show popover in parent and return
      const status = err?.status ?? (err as { status?: number })?.status;
      if (status === 422 && onSmsTokenError) {
        const operationStatus = body?.operationStatus ?? body?.operation_status;
        if (operationStatus === 'sms_token') {
          const fullMessages = body?.fullMessages ?? body?.full_messages;
          const message = Array.isArray(fullMessages) && fullMessages.length > 0
            ? fullMessages.join(' ')
            : (body?.error ?? 'Invalid or expired token.');
          onSmsTokenError(message);
          setCode(['', '', '', '']);
          return;
        }
      }

      // 422 with timer_ends_at (e.g. rate limit from POST /mba/users or PUT /my/phone_number): update parent timer so resend modal shows correct cooldown
      // API returns timer in errors (array or single string); support both camelCase and snake_case
      const fromErrors = (errors: Record<string, unknown> | null | undefined): string | null => {
        if (!errors) return null;
        const raw = (errors.timerEndsAt ?? errors.timer_ends_at) as string | string[] | undefined;
        if (raw == null) return null;
        if (Array.isArray(raw)) return raw[0] ?? null;
        return typeof raw === 'string' ? raw : null;
      };
      const timerEndsAtFromError = fromErrors(body?.errors as Record<string, unknown>)
        ?? fromErrors(err?.errors as Record<string, unknown>)
        ?? body?.timerEndsAt?.[0] ?? body?.timer_ends_at?.[0] ?? err?.timerEndsAt?.[0] ?? err?.timer_ends_at?.[0] ?? null;

      if (timerEndsAtFromError && onTimerEndsAtChange) {
        onTimerEndsAtChange(timerEndsAtFromError);
      }

      // Try to extract error message from different error formats
      let errorMessage: string | null = null;

      if (body?.fullMessages && Array.isArray(body.fullMessages)) {
        errorMessage = body.fullMessages[0] || null;
      } else if (body?.full_messages && Array.isArray(body.full_messages)) {
        errorMessage = body.full_messages[0] || null;
      } else if (body?.error && typeof body.error === 'string') {
        errorMessage = body.error;
      } else if (err?.fullMessages && Array.isArray(err.fullMessages)) {
        errorMessage = err.fullMessages[0] || null;
      } else if (err?.full_messages && Array.isArray(err.full_messages)) {
        errorMessage = err.full_messages[0] || null;
      } else if (err?.error && typeof err.error === 'string') {
        errorMessage = err.error;
      } else if (err?.message && typeof err.message === 'string' && err.message !== 'VALIDATION_ERROR' && err.message !== 'EMAIL_REQUIRED') {
        errorMessage = err.message;
      } else if (err?.errors?.confirmation_code && Array.isArray(err.errors.confirmation_code)) {
        errorMessage = err.errors.confirmation_code[0] || null;
      }

      // Set error message or use default
      setVerificationError(errorMessage || 'Failed to verify code. Please try again.');
      setCode(['', '', '', '']);
    } finally {
      verificationInProgressRef.current = false;
      setIsVerifying(false);
    }
  }, [loading, parentLoading, isVerifying, onVerify, clickSource, onSmsTokenError, onTimerEndsAtChange]);

  // Auto-verify when 4 digits are entered (ref guard prevents duplicate requests when effect re-runs e.g. in Strict Mode)
  useEffect(() => {
    const fullCode = code.join('');
    if (
      fullCode.length === 4
      && fullCode.match(/^\d{4}$/)
      && !loading
      && !parentLoading
      && !isVerifying
      && !verificationInProgressRef.current
    ) {
      handleVerifyCode(fullCode);
    }
  }, [code, loading, parentLoading, handleVerifyCode, isVerifying]);

  // Handle errors - clear code on confirmation code errors
  useEffect(() => {
    const focusFirst = () => {
      if (isAndroid && singleInputRef.current) {
        focusInput(singleInputRef.current);
      } else {
        focusInput(inputRefs.current[0]);
      }
    };
    if (phoneVerificationError && clearError) {
      setCode(['', '', '', '']);
      setTimeout(focusFirst, 100);
      return;
    }
    const displayError = verificationError || parentError;
    if (displayError && clearError) {
      setCode(['', '', '', '']);
      setTimeout(focusFirst, 100);
    }
  }, [verificationError, phoneVerificationError, parentError, clearError, focusInput]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (loading || parentLoading) return;
    if (isAndroid && singleInputRef.current) {
      setTimeout(() => focusInput(singleInputRef.current), 100);
    } else if (inputRefs.current[0]) {
      setTimeout(() => focusInput(inputRefs.current[0]), 100);
    }
  }, [focusInput]);

  // Handle OTP autofill from Chrome/Safari - monitor input value changes (not used on Android: single input handles it)
  useEffect(() => {
    if (isAndroid) return undefined;
    const firstInput = inputRefs.current[0];
    if (!firstInput) {
      return undefined;
    }

    const handleAutofill = (event?: Event) => {
      const inputValue = firstInput.value;
      // If first input has multiple digits (OTP autofill), split them
      if (inputValue && inputValue.length > 1) {
        const digits = inputValue.replace(/\D/g, '').slice(0, 4);
        if (digits.length === 4) {
          const newCode = digits.split('');
          setCode(newCode);
          focusInput(inputRefs.current[3]);
          // Prevent default to avoid duplicate handling
          if (event) {
            event.preventDefault();
          }
        }
      }
    };

    // Listen for input events (fires on autofill in Chrome)
    firstInput.addEventListener('input', handleAutofill);
    // Listen for beforeinput events (fires before input in some browsers)
    firstInput.addEventListener('beforeinput', (e: any) => {
      if (e.data && e.data.length > 1) {
        handleAutofill(e);
      }
    });

    return () => {
      firstInput.removeEventListener('input', handleAutofill);
    };
  }, [focusInput]);

  // Handle input change
  const handleInputChange = useCallback((index: number, value: string) => {
    // Handle OTP autofill - if multiple digits are entered (e.g., "1234"), split them
    if (value.length > 1) {
      // Extract only digits
      const digits = value.replace(/\D/g, '').slice(0, 4);
      if (digits.length === 4) {
        // Split into array and set all inputs
        const newCode = digits.split('');
        setCode(newCode);
        focusInput(inputRefs.current[3]);
        return;
      }
      // If less than 4 digits, fill from current index
      const newCode = [...code];
      for (let i = 0; i < digits.length && (index + i) < 4; i++) {
        newCode[index + i] = digits[i];
      }
      setCode(newCode);
      const nextIndex = Math.min(index + digits.length, 3);
      focusInput(inputRefs.current[nextIndex]);
      return;
    }

    // Allow only digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input (rAF + preventScroll to reduce keyboard hide/show on iOS)
    if (value && index < 3) {
      focusInput(inputRefs.current[index + 1]);
    }
  }, [code, focusInput]);

  // Handle click - move cursor to end of value
  const handleClick = useCallback((index: number, e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    // Set cursor to end of value
    setTimeout(() => {
      const valueLength = target.value.length;
      target.setSelectionRange(valueLength, valueLength);
    }, 0);
  }, []);

  // Handle focus - move cursor to end of value
  const handleFocus = useCallback((index: number, e: React.FocusEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    // Set cursor to end of value when input gets focus
    setTimeout(() => {
      const valueLength = target.value.length;
      target.setSelectionRange(valueLength, valueLength);
    }, 0);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const currentValue = code[index];

    // Backspace handling
    if (e.key === 'Backspace') {
      // If input has value and cursor is at the end, clear it
      if (currentValue && target.selectionStart === target.value.length) {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
        if (index > 0) {
          focusInput(inputRefs.current[index - 1]);
        }
        e.preventDefault();
        return;
      }
      // If input is empty, move to previous input
      if (!currentValue && index > 0) {
        focusInput(inputRefs.current[index - 1]);
        e.preventDefault();
        return;
      }
    }

    // Delete handling
    if (e.key === 'Delete') {
      if (currentValue) {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
        e.preventDefault();
        return;
      }
      if (!currentValue && index < 3) {
        focusInput(inputRefs.current[index + 1]);
        e.preventDefault();
        return;
      }
    }

    // Arrow keys for navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(inputRefs.current[index - 1]);
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      focusInput(inputRefs.current[index + 1]);
      e.preventDefault();
    }
  }, [code, focusInput]);

  // Handle paste
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{4}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      focusInput(inputRefs.current[3]);
    }
  }, [focusInput]);

  // Android: single input — no focus change, avoids keyboard hide/show
  const handleSingleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    const arr = raw.split('');
    setCode([...arr, '', '', ''].slice(0, 4));
  }, []);

  const handleSingleInputPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').trim().replace(/\D/g, '').slice(0, 4);
    if (pasted.length > 0) {
      e.preventDefault();
      const arr = pasted.split('');
      setCode([...arr, '', '', ''].slice(0, 4));
    }
  }, []);

  // Handle resend code
  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0 || loading || parentLoading || !phoneNumber) {
      return;
    }

    try {
      // sendSmsCode returns timerEndsAt from API response
      // Pass isResendAttempt=true for analytics tracking
      const newTimerEndsAt = await sendSmsCode(phoneNumber, true);
      // Timer will be set from API response (timer_ends_at)
      // Notify parent to save timerEndsAt
      if (newTimerEndsAt && onTimerEndsAtChange) {
        onTimerEndsAtChange(newTimerEndsAt);
      }
      // Reset code inputs
      setCode(['', '', '', '']);
      setVerificationError(null);
      setIsResendModalVisible(false);
      setTimeout(() => {
        if (isAndroid && singleInputRef.current) {
          focusInput(singleInputRef.current);
        } else {
          focusInput(inputRefs.current[0]);
        }
      }, 100);
    } catch (_err) {
      // Error is handled in the hook
    }
  }, [resendCooldown, loading, parentLoading, phoneNumber, sendSmsCode, onTimerEndsAtChange, focusInput]);

  // Handle edit number
  const handleEditNumber = useCallback(() => {
    if (onEditNumber) {
      onEditNumber();
    } else if (onBack) {
      onBack();
    }
    setIsResendModalVisible(false);
  }, [onEditNumber, onBack]);

  // Get error message to display
  const errorMessage = useMemo(() => {
    // phoneVerificationError is now a string from usePhoneVerification hook
    if (phoneVerificationError) {
      return phoneVerificationError;
    }

    // Handle verificationError (from onVerify callback) and parentError
    const displayError = verificationError || parentError;
    if (!displayError) return null;

    // Check if displayError is a string
    if (typeof displayError === 'string') {
      return displayError;
    }

    return null;
  }, [verificationError, phoneVerificationError, parentError]);

  // Show spinner only if loading and not verifying (verification errors should hide spinner)
  // If isVerifying is false, spinner should be hidden even if parentLoading is true
  const isLoading = (loading || parentLoading) && isVerifying;

  // Default subtitle
  const subtitle = subtitleKey
    ? t(subtitleKey)
    : `${ t('smsVerification.we_sent_code_to') } ${ formatPhoneNumber(phoneNumber) }`;

  return (
    <div className={ classes.phoneVerificationContainer }>
      {/* Icon */}
      <div className={ classes.phoneLoginIconContainer }>
        <div className={ classes.phoneLoginIconCircle }>
          <SvgIcon id="verification_phone_icon" />
        </div>
      </div>
      <div className={ classes.phoneVerificationContent }>

        {/* Title */}
        <div className={ classes.phoneLoginTitleContainer }>
          <h2 className={ classes.phoneLoginTitle }>
            {t(titleKey)}
          </h2>
          <p className={ classes.phoneLoginSubtitle }>
            {subtitle}
          </p>
        </div>

        {/* Code Input Container */}
        <div className={ classes.phoneVerificationCodeContainer }>
          {/* Loading Spinner - positioned absolutely in center */}
          {/* Only show spinner if isVerifying is true (actively verifying) */}
          {isVerifying && (
            <div className={ classes.phoneVerificationLoadingWrapper }>
              <Spiner
                loaderColor={ shared.appColor }
                loaderSize="44px"
                loaderBorder="4px"
                loaderContainerSize="44px"
                className={ classes.phoneVerificationLoadingSpinner }
              />
            </div>
          )}
          <div className={ classNames(
            classes.phoneVerificationCodeInputs,
            isAndroid && classes.phoneVerificationCodeInputsSingle
          ) }
          >
            {!isVerifying && isAndroid ? (
              <>
                {/* Android: display cells + single overlay input to avoid focus switching and keyboard jump */}
                {code.map((digit, index) => (
                  <div
                    key={ index }
                    className={ classNames(
                      classes.phoneVerificationCodeInput,
                      classes.phoneVerificationCodeInputCell,
                      digit !== '' ? classes.phoneVerificationCodeInputFilled : null,
                      errorMessage ? classes.phoneVerificationCodeInputError : null
                    ) }
                    aria-hidden
                  >
                    {digit}
                  </div>
                ))}
                <input
                  ref={ singleInputRef }
                  type="text"
                  inputMode="numeric"
                  maxLength={ 4 }
                  value={ code.join('') }
                  onChange={ handleSingleInputChange }
                  onPaste={ handleSingleInputPaste }
                  autoComplete="one-time-code"
                  className={ classNames(
                    classes.phoneVerificationCodeInput,
                    classes.phoneVerificationCodeInputOverlay
                  ) }
                  disabled={ isVerifying }
                  aria-label={ t('smsVerification.verification_code_required') }
                />
              </>
            ) : !isVerifying && code.map((digit, index) => (
              <input
                key={ index }
                ref={ (el) => { inputRefs.current[index] = el; } }
                type="text"
                inputMode="numeric"
                maxLength={ index === 0 ? 4 : 1 }
                value={ digit }
                onChange={ (e) => handleInputChange(index, e.target.value) }
                onInput={ (e) => {
                  const target = e.target as HTMLInputElement;
                  const inputValue = target.value;
                  if (inputValue.length > 1) {
                    handleInputChange(index, inputValue);
                  }
                } }
                onBeforeInput={ (e: any) => {
                  if (e.data && e.data.length > 1 && index === 0) {
                    const digits = e.data.replace(/\D/g, '').slice(0, 4);
                    if (digits.length === 4) {
                      e.preventDefault();
                      handleInputChange(index, digits);
                    }
                  }
                } }
                onClick={ (e) => handleClick(index, e) }
                onFocus={ (e) => handleFocus(index, e) }
                onKeyDown={ (e) => handleKeyDown(index, e) }
                onPaste={ index === 0 ? handlePaste : undefined }
                autoComplete={ index === 0 ? 'one-time-code' : 'off' }
                className={ classNames(
                  classes.phoneVerificationCodeInput,
                  digit !== '' ? classes.phoneVerificationCodeInputFilled : null,
                  errorMessage ? classes.phoneVerificationCodeInputError : null
                ) }
                disabled={ isVerifying }
              />
            ))}
          </div>
          {errorMessage && (
          <div className={ classes.phoneLoginError }>
            {errorMessage}
          </div>
          )}
          {/* Resend Code Button - Opens Modal */}
          {!isVerifying && (
          <button
            type="button"
            onClick={ () => setIsResendModalVisible(true) }
            className={ classNames(
              classes.phoneVerificationResendLink,
              (isVerifying) ? classes.phoneVerificationResendLinkDisabled : null
            ) }
            disabled={ isVerifying }
          >
            {t('smsVerification.didnt_receive_code')}
          </button>
          )}
        </div>
      </div>

      {/* Resend Options Modal */}
      <ResendOptionsModal
        visible={ isResendModalVisible }
        onClose={ () => setIsResendModalVisible(false) }
        onResendCode={ handleResendCode }
        onEditNumber={ handleEditNumber }
        smsCooldown={ resendCooldown }
        loading={ isLoading }
        t={ t }
      />
    </div>
  );
}

export default PhoneVerificationBase;
